import { useFeatureFlagEnabled } from "posthog-js/react";
import { cn } from "~/lib/utils";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router";
import { featureFlags } from "~/config/featureFlags";
import { PlatformRole } from "~/config/types";
import { useUserContext } from "~/hooks/useUserContext";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "~/atoms/navigation-menu";

export const MainNav = () => {
  const { t } = useTranslation();
  const { user } = useUserContext();
  const location = useLocation();
  const canManagePlatform =
    user.role === PlatformRole.coordinator ||
    user.role === PlatformRole.superAdmin;
  const isWorkflowTemplatesEnabled = useFeatureFlagEnabled(
    featureFlags.workflowTemplates,
  );

  return (
    <NavigationMenu className="[&>div.absolute]:-right-15 [&>div.absolute]:left-auto">
      <NavigationMenuList>
        <NavigationMenuItem>
          <Link to="/">{t(($) => $.dashboard.header)}</Link>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <Link to="/documents?status=published">
            {t(($) => $.library.header)}
          </Link>
        </NavigationMenuItem>
        <NavigationMenuItem>
          <Link to="/chart">{t(($) => $.chart.header)}</Link>
        </NavigationMenuItem>
        {(user.role === PlatformRole.contributor ||
          (!isWorkflowTemplatesEnabled &&
            user.role !== PlatformRole.reader)) && (
          <NavigationMenuItem>
            <Link to="/workflows">
              {t(($) => $.commons.workflows.workflow, {
                count: 2,
              })}
            </Link>
          </NavigationMenuItem>
        )}
        {isWorkflowTemplatesEnabled && canManagePlatform && (
          <NavigationMenuItem>
            <NavLink
              to="/workflows"
              onClick={(e) => e.stopPropagation()}
              className="contents"
            >
              <NavigationMenuTrigger
                className="text-base"
                data-active={
                  location.pathname.startsWith("/workflows") ||
                  location.pathname.startsWith("/workflow-templates")
                    ? ""
                    : undefined
                }
              >
                {t(($) => $.commons.workflows.workflow, { count: 2 })}
              </NavigationMenuTrigger>
            </NavLink>
            <NavigationMenuContent>
              <ul className="grid w-44 gap-1 p-1">
                <li>
                  <NavigationMenuLink asChild>
                    <NavLink to="/workflows">
                      {t(($) => $.commons.workflows.ongoingAndUpcoming)}
                    </NavLink>
                  </NavigationMenuLink>
                </li>
                <li>
                  <NavigationMenuLink asChild>
                    <NavLink to="/workflow-templates">
                      {t(($) => $.workflowTemplates.navLabel)}
                    </NavLink>
                  </NavigationMenuLink>
                </li>
              </ul>
            </NavigationMenuContent>
          </NavigationMenuItem>
        )}
      </NavigationMenuList>
    </NavigationMenu>
  );
};

const Link = ({ to, children }: { to: string; children: React.ReactNode }) => {
  const location = useLocation();
  const isActive =
    to === "/" ? location.pathname === to : location.pathname.startsWith(to);

  return (
    <NavigationMenuLink
      className={cn(navigationMenuTriggerStyle(), "cursor-pointer text-base")}
      active={isActive}
      asChild
    >
      <NavLink to={to}>{children}</NavLink>
    </NavigationMenuLink>
  );
};
